import { KeyIcon } from '@components/Icons/KeyIcon';
import { MailIcon } from '@components/Icons/MailIcon';
import { useResendCooldown } from '@lib/hooks/auth/useResendCooldown';
import { isEmailValid } from '@lib/utils/email';
import { Box } from '@mui/material';
import { userStore } from '@stores/User';
import { useState } from 'react';
import { AuthButton } from '../AuthButton';
import { AuthErrorMessage } from '../AuthErrorMessage';
import { AuthField } from '../AuthField';
import { BackToLogin } from '../BackToLogin';
import css from './PasswordResetRequestForm.module.scss';

export const PasswordResetRequestForm = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [passwordResetSuccess, setPasswordResetSuccess] = useState(false);
  const cooldown = useResendCooldown();

  const submit = () => {
    if (!isEmailValid(email)) {
      setError('Invalid email');
      return;
    }

    setLoading(true);
    setError(null);
    userStore
      .requestPasswordReset(email)
      .catch((e) => setError(e.message))
      .then(() => {
        cooldown.start();
        if (!error) setPasswordResetSuccess(true);
      })
      .finally(() => setLoading(false));
  };

  if (passwordResetSuccess && !error) {
    return (
      <Box className={css.passwordResetFormContainer}>
        <Box className={css.passwordResetFormBox}>
          <Box className={css.passwordResetFormIcon}>
            <MailIcon />
          </Box>
          <Box className={css.passwordResetHeaderText}>
            <h2>Check your email</h2>
            <span>We sent a password reset link to {email}</span>
          </Box>
          {error && <AuthErrorMessage message={error} />}
          <Box className={css.passwordResetFooterText}>
            <span>
              Email not in your inbox or spam?
              <br />
              {loading || cooldown.active ? (
                `Please wait ${loading ? 60 : cooldown.secondsLeft} seconds to be able to resend.`
              ) : (
                <>
                  Click here to{' '}
                  <a href="#" onClick={submit}>
                    resend
                  </a>
                  .
                </>
              )}
            </span>
          </Box>
        </Box>
      </Box>
    );
  }
  return (
    <Box className={css.passwordResetFormContainer}>
      <Box className={css.passwordResetFormBox}>
        <Box className={css.passwordResetFormIcon}>
          <KeyIcon />
        </Box>
        <Box className={css.passwordResetHeaderText}>
          <h2>Forgot password?</h2>
          <span>No worries, we'll send you reset instructions.</span>
        </Box>
        <form
          className={css.passwordResetFormInputContainer}
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          <AuthField
            fullWidth
            label="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            autoFocus
            required
          />
          {error && (
            <AuthErrorMessage
              message={
                error === 'You have exceeded the request limit'
                  ? `You've recently requested a password reset link.` +
                    (cooldown.active
                      ? ` Please wait ${cooldown.secondsLeft} seconds before retrying.`
                      : '')
                  : error
              }
            />
          )}
          <AuthButton type="submit" disabled={loading || cooldown.active}>
            {loading ? 'Sending reset link' : 'Reset password'}
          </AuthButton>
          <BackToLogin />
        </form>
      </Box>
    </Box>
  );
};
